import windowSize from "../services/WindowSize.js";
/**
 * Handles smooth scrolling helpers.
 *
 * @package AGDS
 * @category Modules
 * @module Scroll
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class Scroll {
    /**
     * Creates a scroll helper instance.
     *
     * @param {Object} options
     * @param {HTMLElement} [options.header]
     */
    constructor(options = {}) {
        this.header = options.header;
    }

    /**
     * Initializes scroll features.
     *
     * @returns {void}
     */
    init() {
        this.smoothScrollToAnchor();

        // Tablet
        this.unsubWinSizeTablet = windowSize.onBelow(
            992,
            () => {
                if (this.onScrollHandler) {
                    return;
                }

                this.onScrollHandler = () => this.onScroll();

                window.addEventListener("scroll", this.onScrollHandler, {
                    passive: true,
                });
            },
            { immediate: true },
        );

        // Mobile
        this.unsubWinSizeMobile = windowSize.onBelow(
            768,
            () => {
                this.checkTableOverflow();
                this.swipeIcon();
            },
            { immediate: true },
        );
    }

	/**
	 * Checks if a table is horizontally scrollable and adds a class to the wrapper.
	 *
	 * @returns {void}
	 */
    checkTableOverflow() {
        const wrappers = document.querySelectorAll(".table-wrap");

        wrappers.forEach((wrap) => {
            const table = wrap.querySelector("table");
            const iconWrap = wrap.parentElement.querySelector(".svg-icon-wrap") || wrap.nextElementSibling;
            // Якщо svg-icon-wrap лежить всередині container на одному рівні з table-wrap:
            // const iconWrap = wrap.closest('.container')?.querySelector('.svg-icon-wrap');

            if (!table) return;

            const isOverflowing = table.scrollWidth > wrap.clientWidth;

            if (isOverflowing) {
                wrap.classList.add("is-scrollable");
                if (iconWrap && iconWrap.classList.contains("svg-icon-wrap")) {
                    iconWrap.classList.add("is-visible");
                }
            } else {
                wrap.classList.remove("is-scrollable");
                if (iconWrap && iconWrap.classList.contains("svg-icon-wrap")) {
                    iconWrap.classList.remove("is-visible");
                }
            }
        });
    }

    /**
     * Initialize swipe hint icons for horizontally scrollable containers.
     *
     * Each swipe icon has its own persistent localStorage state.
     * The icon is permanently dismissed when the related container is
     * horizontally scrolled or the icon itself is clicked/tapped.
     *
     * @returns {void}
     */
    swipeIcon() {
        if (this.isSwipeIconInitialized) {
            return;
        }

        const swipeEls = document.querySelectorAll(".swipe-icon-wrap");

        if (!swipeEls.length) {
            return;
        }

        this.isSwipeIconInitialized = true;

        const storagePrefix = "swipe-icon";
        const timers = new Map();

        /**
         * Find the nearest horizontally scrollable parent element.
         *
         * @param {HTMLElement} element - Starting element.
         * @returns {HTMLElement|null} Scrollable parent element.
         */
        const getScrollContainer = (element) => {
            let parent = element.parentElement;

            while (parent) {
                const { overflowX } = window.getComputedStyle(parent);
                const isScrollable =
                    ["auto", "scroll", "overlay"].includes(overflowX) && parent.scrollWidth > parent.clientWidth;

                if (isScrollable) {
                    return parent;
                }

                parent = parent.parentElement;
            }

            return null;
        };

        /**
         * Get a stable storage key for the swipe icon.
         *
         * @param {HTMLElement} swipe - Swipe icon element.
         * @param {number} index - Icon index.
         * @returns {string} Storage key.
         */
        const getStorageKey = (swipe, index) => {
            const id = swipe.dataset.swipeId || `index-${index}`;

            return `${storagePrefix}:${window.location.pathname}:${id}`;
        };

        /**
         * Check whether the swipe icon was already dismissed.
         *
         * @param {string} storageKey - Local storage key.
         * @returns {boolean} Whether the icon was dismissed.
         */
        const isDismissed = (storageKey) => {
            return localStorage.getItem(storageKey) === "1";
        };

        /**
         * Permanently dismiss a swipe icon.
         *
         * @param {HTMLElement} swipe - Swipe icon element.
         * @param {string} storageKey - Local storage key.
         * @returns {void}
         */
        const dismissSwipe = (swipe, storageKey) => {
            swipe.classList.remove("_active");

            localStorage.setItem(storageKey, "1");

            const timer = timers.get(swipe);

            if (timer) {
                clearTimeout(timer);
                timers.delete(swipe);
            }

            observer.unobserve(swipe);
        };

        /**
         * Hide the icon automatically after it becomes visible.
         */
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting }) => {
                if (!isIntersecting || timers.has(target)) {
                    return;
                }

                const timer = window.setTimeout(() => {
                    target.classList.remove("_active");
                    timers.delete(target);
                    observer.unobserve(target);
                }, 3000);

                timers.set(target, timer);
            });
        });

        swipeEls.forEach((swipe, index) => {
            const scrollContainer = getScrollContainer(swipe);
            const storageKey = getStorageKey(swipe, index);

            // The icon cannot work without a scrollable container.
            if (!scrollContainer) {
                swipe.classList.remove("_active");

                return;
            }

            // Do not show the icon again after it was dismissed.
            if (isDismissed(storageKey)) {
                swipe.classList.remove("_active");

                return;
            }

            let initialScrollLeft = scrollContainer.scrollLeft;

            /**
             * Handle horizontal scrolling of the related container.
             *
             * @returns {void}
             */
            const handleScroll = () => {
                const currentScrollLeft = scrollContainer.scrollLeft;

                if (currentScrollLeft === initialScrollLeft) {
                    return;
                }

                initialScrollLeft = currentScrollLeft;

                dismissSwipe(swipe, storageKey);
            };

            /**
             * Handle clicking or tapping the swipe icon.
             *
             * @returns {void}
             */
            const handleClick = () => {
                dismissSwipe(swipe, storageKey);
            };

            scrollContainer.addEventListener("scroll", handleScroll, {
                passive: true,
            });

            swipe.addEventListener("click", handleClick);

            observer.observe(swipe);
        });
    }

    /**
     * Updates the position of the dot within the steps container based on scroll progress.
     *
     * @returns {void}
     */
    onScroll() {
        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                this.updateStepDotPosition();
                this.ticking = false;
            });
            this.ticking = true;
        }
    }

    /**
     * Calculates and updates the dot position
     * for the steps container based on scroll progress.
     */
    updateStepDotPosition() {
        const elements = document.querySelectorAll(".steps");
        if (!elements.length) return;

        elements.forEach((element) => {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            // Check if the element is currently visible within the viewport
            if (rect.top <= viewportHeight && rect.bottom >= 0) {
                // Calculate the scroll progress (from 0 to 1)
                let progress = (viewportHeight - rect.top) / (viewportHeight + rect.height);

                // Clamp the progress strictly between 0 and 1 to prevent the dot from overflowing out of bounds
                progress = Math.max(0, Math.min(1, progress));

                const maxPosition = element.offsetHeight;
                const dotPosition = progress * maxPosition;

                // Update the CSS custom property on the element to move the dot
                element.style.setProperty("--dot-position", `${dotPosition}px`);
            }
        });
    }

    /**
     * Smoothly scrolls anchor links and offsets by header height.
     *
     * @returns {void}
     */
    smoothScrollToAnchor() {
        const anchorLinks = document.querySelectorAll('a[href*="#"]:not([data-fancybox])');
        if (!anchorLinks.length) return;

        anchorLinks.forEach((anchor) => {
            anchor.addEventListener("click", (event) => {
                const href = anchor.getAttribute("href");
                const hash = href.split("#")[1];

                if (hash && document.getElementById(hash)) {
                    event.preventDefault();
                    const headerHeight = this.header ? this.header.offsetHeight : 0;
                    const targetOffset = document.getElementById(hash).offsetTop - headerHeight;
                    this.scrollToWindowTop(targetOffset);
                }
            });
        });
    }

    /**
     * Scrolls the window to the given offset.
     *
     * @param {number} top - Top offset.
     * @returns {void}
     */
    scrollToWindowTop(top = 0) {
        window.scrollTo({ top: top, behavior: "smooth" });
    }
}
